import { useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Input } from '../../../../../components/ui/Input';
import { Link } from '../../../../../components/ui/Link';
import { Message, MessageStack, type MessageType } from '../../../../../components/ui/Message';
import { Select } from '../../../../../components/ui/Select';
import { Switch } from '../../../../../components/ui/Switch';
import { Table, type TableColumn } from '../../../../../components/ui/Table';
import { RelationDetailDialog } from '../../../../coupon-management/versions/v1/app/RuleDialogs';
import { newcomerCouponId, referrerCouponId, updateRecommendationCard, useCouponEntities, useCouponStore } from '../../../../shared/couponStore';
import { StackRangeValueCell } from '../../../../shared/StackRangeCell';
import { statusFilterOptions, type RecommendationCard } from '../fixtures';
import { deriveCardStatus, type RecommendationCardStatus } from '../fixtures';
import styles from './RecommendationCardListView.module.css';

/** 推广在已结束与已停用下不可发起，但按钮常驻且置灰，保证各行的操作栏宽度一致。 */
function canPromote(status: RecommendationCardStatus): boolean {
  return status === '进行中' || status === '未开始';
}

/** 状态点：进行中为蓝，未开始为橙，已结束与已停用为灰；与优惠券列表同一形态。 */
function statusDotClass(status: RecommendationCardStatus): string {
  if (status === '进行中') return styles.statusDotActive;
  if (status === '未开始') return styles.statusDotPending;
  return styles.statusDotMuted;
}

/** 只有已结束的文字弱化；已停用是被人工停掉，仍用常规文字色。 */
function statusTextClass(status: RecommendationCardStatus): string {
  return status === '已结束' ? `${styles.statusText} ${styles.statusTextMuted}` : styles.statusText;
}

export function RecommendationCardListView({ onCreate, onEdit, onOpenRules }: { onCreate: () => void; onEdit: (card: RecommendationCard) => void; onOpenRules: (couponId: number) => void }) {
  /* 卡片列表与它派生的两张奖励券共用同一份数据，因此从 store 读而不是页内 state。 */
  const { cards, groups } = useCouponStore();
  const entities = useCouponEntities();
  const [nameDraft, setNameDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [notice, setNotice] = useState<{ message: string; type?: MessageType } | null>(null);
  /* 叠加范围就地查看：点单元格在当前页弹关系明细，不跳规则页。 */
  const [relationId, setRelationId] = useState<number | null>(null);
  const relationCoupon = relationId != null ? entities.find((coupon) => coupon.id === relationId) : undefined;
  const normalizedName = name.trim();
  const filteredCards = cards.filter((card) => (!normalizedName || card.name.includes(normalizedName))
    && (!status || deriveCardStatus(card) === status));

  function showNotice(message: string, type: MessageType = 'primary') {
    setNotice({ message, type });
  }

  function queryCards() {
    setName(nameDraft);
    setStatus(statusDraft);
    setPage(1);
  }

  /* 「启用/停用」开关只改人工标志，活动窗口不动；时间派生出来的状态（进行中／未开始／已结束）会在重新启用后自动回来。 */
  function toggleClosed(card: RecommendationCard, enabled: boolean) {
    updateRecommendationCard(card.id, { closed: !enabled });
    showNotice(enabled ? `已启用“${card.name}”` : `已停用“${card.name}”`);
  }

  /*
   * 推广在已结束与已停用下不可用。这里不用原生 disabled（会吞掉点击），
   * 而是用 Link 的 softDisabled 保留点击能力，点下去给出不可用的具体原因。
   */
  function promote(card: RecommendationCard) {
    const status = deriveCardStatus(card);
    if (status === '已结束') {
      showNotice(`“${card.name}”活动已结束，无法推广`, 'warning');
      return;
    }
    if (status === '已停用') {
      showNotice(`“${card.name}”已停用，如需推广请先启用该推荐卡`, 'warning');
      return;
    }
    showNotice(`已发起“${card.name}”推广`);
  }

  function runAction(card: RecommendationCard, key: string) {
    if (key === 'promote') {
      promote(card);
      return;
    }
    /* 编辑直接打开编辑页（奖励设置里的叠加范围在那里展示）；授权页面尚未设计，只回显反馈。 */
    if (key === 'edit') {
      onEdit(card);
      return;
    }
    showNotice(`已打开“${card.name}”的授权教练设置`);
  }

  const columns: TableColumn<RecommendationCard>[] = [
    { key: 'name', title: '名称', width: 230 },
    { key: 'startTime', title: '开始时间', width: 150 },
    { key: 'endTime', title: '结束时间', width: 140 },
    {
      key: 'shareScope',
      render: (card) => {
        /* 已结束、已停用的卡片不再有可改的分享奖励，整格压成禁用色且不可点。 */
        const current = deriveCardStatus(card);
        const locked = current === '已结束' || current === '已停用';
        return <Link disabled={locked} onClick={() => showNotice(`已打开“${card.name}”的分享奖励设置`)} type="default">{card.shareScope}</Link>;
      },
      title: '分享奖励',
      width: 120,
    },
    {
      align: 'right',
      key: 'authorizedCoaches',
      render: (card) => (card.authorizedCoaches > 0
        /* 常态用正文色，指向时才转主题色，避免整列都是蓝的。 */
        ? <Link onClick={() => showNotice(`已打开“${card.name}”的授权教练设置`)} type="default">{String(card.authorizedCoaches)}</Link>
        : <Link muted onClick={() => showNotice(`已打开“${card.name}”的授权教练设置`)} type="default">未授权</Link>),
      title: '授权教练',
      width: 100,
    },
    {
      /* 二级表头：父列「叠加范围」只做分组与说明，两个子列各自显示一张奖励券的可叠加数量。 */
      children: [
        {
          align: 'right',
          key: 'relation-referrer',
          render: (card) => {
            const coupon = entities.find((item) => item.id === referrerCouponId(card.id));
            return coupon ? <StackRangeValueCell coupon={coupon} entities={entities} groups={groups} onOpen={setRelationId} /> : null;
          },
          title: '老生券',
          width: 90,
        },
        {
          align: 'right',
          key: 'relation-newcomer',
          render: (card) => {
            const coupon = entities.find((item) => item.id === newcomerCouponId(card.id));
            return coupon ? <StackRangeValueCell coupon={coupon} entities={entities} groups={groups} onOpen={setRelationId} /> : null;
          },
          title: '新生券',
          width: 90,
        },
      ],
      /* 列头问号说明这一列只读、只显示数量。 */
      help: '显示可与这张奖励券叠加的优惠券数量；叠加关系在「叠加规则设置」中配置，这里只做查看。',
      key: 'relation',
      title: '叠加范围',
    },
    {
      key: 'status',
      render: (card) => { const current = deriveCardStatus(card); return <span className={statusTextClass(current)}><span aria-hidden="true" className={statusDotClass(current)} />{current}</span>; },
      title: '状态',
      width: 110,
    },
    {
      key: 'enabled',
      render: (card) => {
        /* 已结束的活动没有启停意义，开关直接禁用。 */
        const ended = deriveCardStatus(card) === '已结束';
        return <Switch checked={!card.closed} disabled={ended} label={`${card.name}启用状态`} onCheckedChange={(next) => toggleClosed(card, next)} size="small" />;
      },
      title: '启用/停用',
      width: 100,
    },
    {
      key: 'actions',
      render: (card) => { const current = deriveCardStatus(card); return <span className={styles.cardActions}>
        <Link onClick={() => runAction(card, 'promote')} softDisabled={!canPromote(current)}>推广</Link>
        <Link onClick={() => runAction(card, 'authorize')}>授权</Link>
        <Link onClick={() => runAction(card, 'edit')}>编辑</Link>
      </span>; },
      title: '操作',
    },
  ];

  return <>
    <div className={styles.filters}>
      <label>名称<Input ariaLabel="电子推荐卡名称" className={styles.filterInput} onKeyDown={(event) => { if (event.key === 'Enter') queryCards(); }} onValueChange={setNameDraft} placeholder="电子推荐卡名称" value={nameDraft} /></label>
      <label>状态<Select ariaLabel="电子推荐卡状态" className={styles.filterSelect} onValueChange={setStatusDraft} options={statusFilterOptions} value={statusDraft} /></label>
      <div className={styles.filterActions}><Button onClick={queryCards}>查询</Button></div>
    </div>
    <div className={styles.toolbar}>
      <Button onClick={onCreate}>新增</Button>
    </div>
    <Table
      ariaLabel="电子推荐卡列表"
      columns={columns}
      emptyText="暂无电子推荐卡"
      pagination={{
        ariaLabel: '电子推荐卡分页',
        onPageChange: setPage,
        onPageSizeChange: (_current, nextPageSize) => { setPageSize(nextPageSize); setPage(1); },
        page,
        pageSize,
        showQuickJumper: true,
        showSizeChanger: true,
        showTotal: (total) => `共${total}条`,
      }}
      rowKey={(card) => card.id}
      rows={filteredCards}
    />
    <MessageStack>{notice ? <Message message={notice.message} onOpenChange={(open) => { if (!open) setNotice(null); }} open type={notice.type} /> : null}</MessageStack>
    <RelationDetailDialog coupon={relationCoupon} entities={entities} groups={groups} key={relationCoupon?.id ?? 'none'} onEdit={(id) => { setRelationId(null); onOpenRules(id); }} onOpenChange={(open) => { if (!open) setRelationId(null); }} open={relationCoupon !== undefined} />
  </>;
}
